import { ActiveCombatEffectKey } from '@hexcrawl/shared-utils';
import type { ActiveCombatEffectDTO } from '@hexcrawl/shared-utils';

/**
 * Player-facing descriptions of timed combat effects, shared by the buff tag drawn over
 * the sprite and the turn-state column in the action bar so both name a buff the same way.
 */
export interface ActiveEffectDisplay {
  /** What the effect does, e.g. "+2 Armor". */
  label: string;
  /** Rounds it still applies for, counting the current one. */
  roundsRemaining: number;
}

const EFFECT_NAMES: Record<ActiveCombatEffectKey, string> = {
  [ActiveCombatEffectKey.TEMP_ARMOR]: 'Armor',
};

/**
 * The unexpired effects on an entity, in the order they were granted. One row per entry:
 * repeat doses of the same item refresh a single entry server-side, so two rows only ever
 * mean two genuinely different sources.
 */
export function summarizeActiveEffects(
  effects: ActiveCombatEffectDTO[] | undefined,
  round: number,
): ActiveEffectDisplay[] {
  return (effects ?? [])
    .filter(effect => round < effect.expiresRound)
    .map(effect => ({
      label: `${effect.amount > 0 ? '+' : ''}${effect.amount} ${EFFECT_NAMES[effect.key]}`,
      roundsRemaining: effect.expiresRound - round,
    }));
}

/** One-line form for the sprite tag: "+2 Armor (3)", buffs separated by spaces. */
export function formatActiveEffectTag(effects: ActiveCombatEffectDTO[] | undefined, round: number): string {
  return summarizeActiveEffects(effects, round)
    .map(effect => `${effect.label} (${effect.roundsRemaining})`)
    .join('  ');
}
